import { useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Star, X } from "lucide-react";
import { mediaUrl, move, uid } from "@/lib/api";
import { Field, ItemControls, Panel, TempBadge, Toggle } from "./Fields";
import { UploadButton } from "./MediaInput";

const Amenities = ({ list, onChange, testid }) => {
  const [v, setV] = useState("");
  const add = () => {
    const t = v.trim();
    if (t && !list.includes(t)) onChange([...list, t]);
    setV("");
  };
  return (
    <div>
      <span className="block text-[0.68rem] uppercase tracking-[0.18em] text-[#A1A1AA] mb-2">Comodidades confirmadas</span>
      <div className="flex gap-2">
        <input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder="Ex.: comodidade confirmada pelo hotel" className="flex-1 h-10 bg-[#0F0F12] border border-white/10 outline-none text-sm text-white px-3" data-testid={`${testid}-input`} />
        <button type="button" onClick={add} className="h-10 px-4 border border-white/15 text-xs uppercase tracking-[0.15em] text-white hover:border-white/50" data-testid={`${testid}-add`}>Adicionar</button>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {list.map((a) => (
          <span key={a} className="inline-flex items-center gap-2 px-3 h-8 text-xs bg-white/5 border border-white/10 text-[#E4E4E7]">
            {a}
            <button type="button" onClick={() => onChange(list.filter((x) => x !== a))} aria-label={`Remover ${a}`}><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
    </div>
  );
};

const Photos = ({ room, set, testid }) => (
  <div>
    <div className="flex items-center justify-between mb-3">
      <span className="text-[0.68rem] uppercase tracking-[0.18em] text-[#A1A1AA]">Fotos ({room.photos.length})</span>
      <UploadButton multiple onUploaded={(urls) => set({ photos: [...room.photos, ...urls], cover: room.cover || urls[0] })} label="Enviar fotos" testid={`${testid}-upload`} />
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {room.photos.map((p, j) => (
        <div key={p + j} className={`relative aspect-[4/3] overflow-hidden border ${room.cover === p ? "border-[#C5A059]" : "border-white/10"}`}>
          <img src={mediaUrl(p)} alt="" className="h-full w-full object-cover" />
          {room.cover === p && <span className="absolute top-1 left-1 text-[0.55rem] uppercase tracking-widest bg-[#C5A059] text-black px-1.5 py-0.5">Capa</span>}
          <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/70">
            <button type="button" onClick={() => set({ photos: move(room.photos, j, -1) })} className="h-8 w-8 flex items-center justify-center text-white" aria-label="Mover para a esquerda"><ChevronLeft className="w-4 h-4" /></button>
            <button type="button" onClick={() => set({ cover: p })} className="h-8 w-8 flex items-center justify-center text-[#E5CF9F]" aria-label="Definir como capa" data-testid={`${testid}-cover-${j}`}><Star className="w-4 h-4" /></button>
            <button type="button" onClick={() => set({ photos: room.photos.filter((_, k) => k !== j), cover: room.cover === p ? room.photos.find((_, k) => k !== j) || "" : room.cover })} className="h-8 w-8 flex items-center justify-center text-red-300" aria-label="Remover foto" data-testid={`${testid}-remove-${j}`}><X className="w-4 h-4" /></button>
            <button type="button" onClick={() => set({ photos: move(room.photos, j, 1) })} className="h-8 w-8 flex items-center justify-center text-white" aria-label="Mover para a direita"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const AccommodationsTab = ({ items, onChange }) => {
  const set = (i, patch) => onChange(items.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const add = () => onChange([...items, { id: uid(), name: "Nova acomodação", description: "", amenities: [], photos: [], cover: "", active: false, temporary: false }]);
  return (
    <Panel
      title="Acomodações"
      desc="Somente categorias ativas aparecem no site. Cadastre apenas informações confirmadas pelo hotel."
      actions={<button type="button" onClick={add} className="inline-flex items-center gap-2 h-10 px-4 text-xs uppercase tracking-[0.15em] bg-white text-black hover:bg-[#E5CF9F]" data-testid="accommodation-add-button"><Plus className="w-4 h-4" /> Nova categoria</button>}
    >
      <div className="space-y-5">
        {items.length === 0 && <p className="text-sm text-[#71717A]">Nenhuma categoria cadastrada.</p>}
        {items.map((r, i) => (
          <div key={r.id} className="border border-white/10 p-5 space-y-5 bg-[#08080A]" data-testid={`admin-accommodation-${i}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-4">
                <Toggle label={r.active ? "Ativa (visível no site)" : "Inativa (oculta)"} checked={r.active} onChange={(v) => set(i, { active: v })} testid={`admin-accommodation-${i}-active`} />
                <Toggle label="Fotos ilustrativas" checked={r.temporary} onChange={(v) => set(i, { temporary: v })} testid={`admin-accommodation-${i}-temporary`} />
                {r.temporary && <TempBadge />}
              </div>
              <ItemControls onUp={() => onChange(move(items, i, -1))} onDown={() => onChange(move(items, i, 1))} onDelete={() => onChange(items.filter((_, k) => k !== i))} testid={`admin-accommodation-${i}`} />
            </div>
            <Field label="Nome" value={r.name} onChange={(v) => set(i, { name: v })} testid={`admin-accommodation-${i}-name`} />
            <Field label="Descrição" multiline value={r.description} onChange={(v) => set(i, { description: v })} testid={`admin-accommodation-${i}-description`} />
            <Amenities list={r.amenities || []} onChange={(v) => set(i, { amenities: v })} testid={`admin-accommodation-${i}-amenity`} />
            <Photos room={r} set={(patch) => set(i, patch)} testid={`admin-accommodation-${i}-photos`} />
          </div>
        ))}
      </div>
    </Panel>
  );
};
